import { Image, makeStyles, Text, tokens } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import TitleText from "../../../components/titleText/TitleText";
import IPageTestimonials from "../../../models/page/pageComponents/IPageTestimonials";
import ITestimonial from "../../../models/testimonial/ITestimonial";
import IClient from "../../../models/client/IClient";
import testimonialService from "../../../services/TestimonialService";
import clientService from "../../../services/ClientService";
import { dolphinTokens } from "../../../utils/Theme";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        padding: "0 2rem",
        maxWidth: "1400px",
        margin: "0 auto",
        alignItems: "center",
        width: "100%",
    },
    grid: {
        display: "flex",
        flexWrap: "wrap",
        gap: "2rem",
        justifyContent: "center",
        marginTop: "0.5rem",
        width: "100%",
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: "20px",
        padding: "2rem",
        flex: "1 1 400px",
        maxWidth: "600px",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        position: "relative",
    },
    content: {
        color: "#444444",
        fontSize: "1rem",
        lineHeight: "1.8",

        "& p": { margin: "0 0 0.75rem 0" },
        "& a": { color: dolphinTokens.colorLightBlue, textDecoration: "none" },
        "& strong": { fontWeight: "600" },
    },
    clientInfo: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "1rem",
        marginTop: "auto",
        paddingTop: "1rem",
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    logo: {
        height: "40px",
        width: "auto",
        objectFit: "contain",
    },
    clientText: {
        display: "flex",
        flexDirection: "column",
        gap: "0.1rem",
    },
    clientName: {
        fontWeight: "700",
        color: dolphinTokens.colorDarkBlue,
    },
    contactName: {
        fontSize: "0.875rem",
        color: "#888888",
    },
});

const TestimonialCard = ({ testimonial, client }: { testimonial: ITestimonial; client?: IClient }) => {
    const styles = useStyles();

    return (
        <div className={styles.card}>
            <div
                className={styles.content}
                dangerouslySetInnerHTML={{ __html: testimonial.content }}
            />
            <div className={styles.clientInfo}>
                {client?.logo?.url && (
                    <Image
                        src={client.logo.url}
                        alt={client.logo.altText}
                        className={styles.logo}
                        loading="lazy"
                    />
                )}
                <div className={styles.clientText}>
                    <Text className={styles.clientName}>{client?.name}</Text>
                    <Text className={styles.contactName}>
                        {testimonial.contact.name}
                        {testimonial.contact.position ? ` · ${testimonial.contact.position}` : ""}
                    </Text>
                </div>
            </div>
        </div>
    );
};

export default function Testimonials(props: IPageTestimonials) {
    const styles = useStyles();
    const [testimonials, setTestimonials] = useState<ITestimonial[]>([]);
    const [clients, setClients] = useState<IClient[]>([]);

    useEffect(() => {
        if (!props.testimonialIds?.length) return;

        Promise.all([testimonialService.getTestimonials(), clientService.getClients()])
            .then(([all, loadedClients]) => {
                const ordered = props.testimonialIds
                    .map((id) => all.find((t) => t.id === id))
                    .filter((t): t is ITestimonial => !!t);
                setTestimonials(ordered);
                setClients(loadedClients);
            })
            .catch(() => {});
    }, [props.testimonialIds]);

    if (!testimonials.length) return null;

    return (
        <div className={styles.container}>
            <TitleText
                title={props.title}
                titleHighlightedWords={props.titleHighlightedWords}
                subtitle={props.subtitle}
                alignment="center"
                width="100%"
            />
            <div className={styles.grid}>
                {testimonials.map((t) => (
                    <TestimonialCard
                        key={t.id}
                        testimonial={t}
                        client={clients.find((client) => client.id === t.clientId)}
                    />
                ))}
            </div>
        </div>
    );
}
